/**
 * @file        src/components/MobileBrandBar.jsx
 * @description Mobile-only brand strip: the Neon Holidays wordmark at the top of
 *              the page, IN NORMAL FLOW. On a phone the sidebar (which is where the
 *              mark otherwise lives) is an off-canvas drawer, so nothing on any
 *              mobile screen said whose site it was.
 *
 *              In flow, not fixed — that is the whole point of this approach. It
 *              cannot overlap page content, it needs no z-index, and no screen has
 *              to reserve room for it; it simply scrolls away with the rest of the
 *              page. The cost is that the brand identifies the site on ARRIVAL
 *              rather than persistently.
 *
 *              Geometry is tied to the fixed hamburger it sits opposite: `pt-4` +
 *              `h-14` put this strip at y 16–72, centred on 44 against that button's
 *              own 20–64 band, so the mark and the menu button read as one row. The
 *              mark is right-aligned, the hamburger is at top-left, and they never
 *              meet.
 *
 *              The strip is 72px tall (16 + 56). A page whose decorated background
 *              should run BEHIND it pulls that background up by exactly that much —
 *              see Home's hero, which uses `-mt-[72px]` plus a matching padding
 *              bump. Change the height here and that pair has to move with it.
 * @author      Kinetika Labs
 * @created     2026-09-10
 * @changelog
 *   - 2026-09-10 Claude — Initial.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo.jsx';

export default function MobileBrandBar() {
  const navigate = useNavigate();

  return (
    // `relative z-10`: still in flow and still scrolls away, but it now PAINTS
    // above the page beneath it. Home's hero pulls its decorated background up
    // under this strip with a negative margin, and without a stacking order the
    // section — which is `relative` itself and comes later in the DOM — would
    // cover the mark.
    <div className="lg:hidden relative z-20 px-4 pt-4">
      {/* `justify-end` puts the mark at the far RIGHT, opposite the fixed
          hamburger at top-left. Nothing else occupies the top-right on mobile
          (the theme toggle that used to be mirrored there was removed), so no
          left-hand clearance is needed here any more.

          h-14 is sized TO THE MARK, not picked for looks: the wordmark's viewBox
          is 600x265, so at w-126 it stands ~56px tall. In a shorter band it
          overflowed the strip and the hero pulled up beneath it clipped the
          "HOLIDAYS" line. `max-h-full` is the belt-and-braces: whatever width is
          set here, the mark can never outgrow its own row again. */}
      <div className="h-14 flex items-center justify-end">
        {/* A real button, not an <a href="/">: the SPA router must handle this or
            the whole bundle reloads. Sized to the mark so the tap target is the
            logo itself and nothing beside it. */}
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="Neon Holidays home"
          className="cursor-pointer"
        >
          <BrandLogo className="w-[126px] max-h-full" />
        </button>
      </div>
    </div>
  );
}
